import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  Collapse
} from '@mui/material';
import {
  QrCode as QrCodeIcon
} from '@mui/icons-material';
import { useInventory } from '../contexts/InventoryContext';
import { useJob } from '../contexts/JobContext';
import { useInventoryPage } from '../contexts/InventoryPageContext';
import BarcodeScanner from './BarcodeScanner';
import { barcodeApi } from '../services/api.service';
import equipmentService from '../services/equipment.service';

export default function ReturnInventory({ open, onClose, selectedItems = [], onReturnComplete }) {
  const { loading, error, clearError } = useInventory();
  const { jobList, currentJob, selectJob } = useJob();
  const {
    selectedWarehouse, setSelectedWarehouse,
    selectedRack, setSelectedRack,
    selectedBay, setSelectedBay,
    selectedJobId, setSelectedJobId,
    warehouses, racks, bays,
    handleBarcodeScan,
    lastScannedCode,
    handleLocationSelect
  } = useInventoryPage();

  // Form state - pre-populate with selected equipment if provided
  const [formData, setFormData] = useState({
    id: selectedItems[0]?.equipment_id || selectedItems[0]?.id || '',
    job: selectedItems[0]?.current_job_id || '',
    warehouse: '',
    rack: '',
    bay: ''
  });
  
  // Items to return - removed, now return immediately

  // Processing state for return
  const [processing, setProcessing] = useState(false);
  
  // Scanner state
  const [showScanner, setShowScanner] = useState(false);

  // Search results
  const [searchResult, setSearchResult] = useState(null);
  
  // Load initial data from shared context
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      warehouse: selectedWarehouse,
      rack: selectedRack,
      bay: selectedBay,
      job: selectedJobId || (currentJob ? currentJob.id : ''),
    }));
  }, [selectedWarehouse, selectedRack, selectedBay, selectedJobId, currentJob]);
  
  // Pre-populate form when selectedItems changes (for modal mode)
  useEffect(() => {
    if (selectedItems && selectedItems.length > 0 && open) {
      const item = selectedItems[0];
      setFormData(prev => ({
        ...prev,
        id: item.equipment_id || item.id || '',
        job: item.current_job_id || prev.job
      }));
    }
  }, [selectedItems, open]);

  // Listen for barcode scans from other components
  useEffect(() => {
    if (lastScannedCode && lastScannedCode !== formData.id) {
      setFormData(prev => ({
        ...prev,
        id: lastScannedCode
      }));
    }
  }, [lastScannedCode]);

  // Look up item when item code changes
  useEffect(() => {
    const lookupItem = async () => {
      if (formData.id) {
        try {
          // Use V2 barcode lookup which supports equipment_id, barcode, and database ID
          const response = await barcodeApi.lookupByBarcode(formData.id);
          if (response.data && response.data.equipment) {
            const item = response.data.equipment;
            setSearchResult(item);
            setFormData(prev => ({
              ...prev,
              description: item.description || '',
              equipment_id: item.equipment_id || item.id
            }));
          } else {
            setSearchResult(null);
          }
        } catch (err) {
          console.error('Error looking up equipment:', err);
          setSearchResult(null);
        }
      } else {
        setSearchResult(null);
        setFormData(prev => ({
          ...prev,
          description: '',
          equipment_id: ''
        }));
      }
    };
    
    lookupItem();
  }, [formData.id]);
  
  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Update shared context
    if (name === 'warehouse') {
      setSelectedWarehouse(value);
    } else if (name === 'rack') {
      setSelectedRack(value);
    } else if (name === 'bay') {
      setSelectedBay(value);
    } else if (name === 'job') {
      setSelectedJobId(value);
      
      // Update current job when job selection changes
      const selectedJob = jobList.find(j => j.id === value);
      if (selectedJob) {
        selectJob(selectedJob);
      }
    }
  };
  
  // Handle immediate return (no list building)
  
  // Handle immediate return submission
  const handleSubmit = async () => {
    try {
      // Validate form
      if (!formData.id) {
        alert('Equipment ID is required');
        return;
      }

      if (!formData.job) {
        alert('Job is required');
        return;
      }

      // Check if equipment is available for return
      if (searchResult && !['assigned', 'in_use'].includes(searchResult.status)) {
        alert(`Equipment cannot be returned. Current status: ${searchResult.status}`);
        return;
      }

      setProcessing(true);

      // Use database ID (UUID), not equipment_id (user-facing code)
      const equipment_id = searchResult?.id || formData.id;
      const return_data = {
        from_job_id: formData.job,
        warehouse_id: formData.warehouse || null,
        rack_id: formData.rack || null,
        bay_id: formData.bay || null,
        notes: 'Returned via return inventory interface',
        returned_by: 'current_user', // TODO: Get from auth context
        return_to_stock: false, // Always keep assigned to job when returning to warehouse
        return_reason: null
      };

      // Use V2 equipment return endpoint
      const response = await equipmentService.returnEquipment(equipment_id, return_data);
      console.log(`Equipment ${equipment_id} returned successfully`);

      // Reset form
      setFormData({
        id: '',
        job: formData.job,
        warehouse: formData.warehouse,
        rack: formData.rack,
        bay: formData.bay
      });

      setSearchResult(null);

      // Call completion callback if provided (parent will handle success notification)
      if (onReturnComplete) {
        onReturnComplete();
      }

    } catch (err) {
      console.error('Error returning equipment:', err);
      alert('Error returning equipment: ' + (err.response?.data?.error || err.message));
    } finally {
      setProcessing(false);
    }
  };
  
  // Handle successful barcode scan
  const handleScanSuccess = (decodedText) => {
    let itemId = decodedText;
    
    // Try to parse QR code JSON data
    try {
      // Check if it's URL encoded JSON (from QR codes)
      const decoded = decodeURIComponent(decodedText);
      const qrData = JSON.parse(decoded);
      
      // Extract the equipment ID from QR code data
      if (qrData.id) {
        itemId = qrData.id;
        console.log('Parsed QR code data:', qrData);
      }
    } catch (e) {
      // Not JSON, use the raw barcode value
      console.log('Using raw barcode value:', decodedText);
    }
    
    // Set the scanned text as the item code
    setFormData(prev => ({
      ...prev,
      id: itemId
    }));
    
    // Share the scanned code with other components
    handleBarcodeScan(decodedText);
    
    // Hide scanner after successful scan
    setShowScanner(false);
  };
  

  return (
    <Box>
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" component="h3" gutterBottom sx={{ mb: 2 }}>
                Add Equipment to Return
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="job-label">Job</InputLabel>
                    <Select
                      labelId="job-label"
                      name="job"
                      value={formData.job}
                      onChange={handleChange}
                      label="Job"
                      required
                    >
                      {jobList.map(job => (
                        <MenuItem key={job.id} value={job.id}>
                          {job.name} ({job.id})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <TextField 
                      name="id"
                      label="QR Code / Equipment ID"
                      value={formData.id}
                      onChange={handleChange}
                      fullWidth
                      required
                      margin="normal"
                    />
                    <IconButton 
                      color="primary" 
                      onClick={() => setShowScanner(!showScanner)}
                      sx={{ ml: 1 }}
                    >
                      <QrCodeIcon />
                    </IconButton>
                  </Box>
                  
                  {/* Barcode Scanner */}
                  <Collapse in={showScanner}>
                    <Box mt={2} mb={2}>
                      <BarcodeScanner 
                        onScanSuccess={handleScanSuccess}
                        onError={(err) => console.error('Scan error:', err)}
                      />
                    </Box>
                  </Collapse>
                </Grid>
                
                {/* Equipment Details Display */}
                {searchResult && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Found:</strong> {searchResult.description}
                      </Typography>
                      {searchResult.manufacturer && (
                        <Typography variant="body2">
                          <strong>Manufacturer:</strong> {searchResult.manufacturer}
                        </Typography>
                      )}
                      {searchResult.model && (
                        <Typography variant="body2">
                          <strong>Model:</strong> {searchResult.model}
                        </Typography>
                      )}
                      {searchResult.serial_number && (
                        <Typography variant="body2">
                          <strong>Serial Number:</strong> {searchResult.serial_number}
                        </Typography>
                      )}
                      {searchResult.barcode && (
                        <Typography variant="body2">
                          <strong>Barcode:</strong> {searchResult.barcode}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        <strong>Status:</strong> {searchResult.status || 'available'}
                      </Typography>
                      {searchResult.warehouse && (
                        <Typography variant="body2">
                          <strong>Current Location:</strong> {searchResult.warehouse} - {searchResult.rack || searchResult.zone} {searchResult.bay ? `- ${searchResult.bay}` : ''}
                        </Typography>
                      )}
                    </Alert>
                  </Grid>
                )}
                
                {/* V2: Individual tracking - no quantity field */}

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    {/* Warehouse Selection */}
                    <FormControl sx={{ flex: 1, minWidth: 180, maxWidth: 250 }}>
                      <InputLabel id="warehouse-label">Warehouse</InputLabel>
                      <Select
                        labelId="warehouse-label"
                        name="warehouse"
                        value={formData.warehouse || ''}
                        onChange={handleChange}
                        label="Warehouse"
                      >
                        <MenuItem value="">
                          <em>None (Optional)</em>
                        </MenuItem>
                        {warehouses.map(warehouse => (
                          <MenuItem key={warehouse.id} value={warehouse.id}>
                            {warehouse.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Rack Selection */}
                    <FormControl sx={{ flex: 1, minWidth: 180, maxWidth: 250 }}>
                      <InputLabel id="rack-label">Rack</InputLabel>
                      <Select
                        labelId="rack-label"
                        name="rack"
                        value={formData.rack || ''}
                        onChange={handleChange}
                        label="Rack"
                      >
                        <MenuItem value="">
                          <em>None (Optional)</em>
                        </MenuItem>
                        {racks.map(rack => (
                          <MenuItem key={rack.id} value={rack.id}>
                            {rack.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Bay Selection */}
                    <FormControl sx={{ flex: 1, minWidth: 180, maxWidth: 250 }}>
                      <InputLabel id="bay-label">Bay</InputLabel>
                      <Select
                        labelId="bay-label"
                        name="bay"
                        label="Bay"
                        value={formData.bay || ''}
                        onChange={handleChange}
                      >
                        <MenuItem value="">
                          <em>None (Optional)</em>
                        </MenuItem>
                        {bays && bays.length > 0 ? (
                          bays.map((bay) => (
                            <MenuItem key={bay.id} value={bay.id}>
                              {bay.name}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>No bays available</MenuItem>
                        )}
                      </Select>
                    </FormControl>

                    {/* Return Button - vertically aligned */}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={processing || !formData.job || !formData.id}
                      sx={{ minWidth: 200, height: 56 }}
                    >
                      {processing ? 'Processing...' : 'Return to Warehouse'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
    </Box>
  );
}